import "./Card.css";
import {
  Stack,
  useTheme,
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Snackbar,
  Alert,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import { Outlet, useOutletContext } from "react-router-dom";
// EndUserView.tsx (add these imports at the top)
import { useLocation } from "react-router-dom";
import { useCallback, useRef, useState, useMemo, useEffect } from "react";
import { ThemeProvider } from "styled-components";
import { AgGridReact } from "ag-grid-react";
import { themeQuartz } from "ag-grid-community";
import { mockOutputView } from "./EndUserViewService";
import MinimizeIcon from "@mui/icons-material/Minimize";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import { ConditionalWrapper } from "../utils/ConditionalWrapper";
import { useArrayActions, useArrayData } from "../../context/ArrayDataContext";
import ChipPopup from "./ChipPopup";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import VersionHistoryDialog from "./VersionHistoryDialog";
import WarningPopup, { parseNumberOrText } from "./UserActionPopup";
import WarningIcon from "@mui/icons-material/Warning";
import { getPlannerView } from "../../api/plannerViewApi";
import { PlannerViewItem } from "../../types/plannerView";
import { getPlannerOutputView } from "../../api/plannerOutputViewApi";
import { PlannerOutputViewResponse } from "../../types/plannerOutputViewReponse";
import { heIL } from "@mui/x-date-pickers/locales";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import {
  formatTwoDecimals,
  formatOneDecimal,
  formatPercentNoDecimals,
  formatValueNoDecimals,
} from "../utils/formatTwoDecimals";
import { hasRolePermission } from "../../constants/roleAccess";
import { gridTheme } from "../../utils/tableUtils";
import { useDispatch } from "react-redux";
// EndUserView.tsx
import { useSnackbar } from "../SnakBar/AppSnackbarProvider";
import ButtonComponent from "../common/button/Button";
import { setApiCall, setSubmittedData } from "../../redux/actions/userSlice";
export type EndUserOutletContext = {
  gridRef: any;
  gridTheme: any;
  rowsPerPage: number;
  setGridReady: (b: boolean) => void;
  sideBar: any;
  loading: boolean;

  columns: any[];
  outputColumns: any[];
  inputRows: any[];
  approvedRows: any[];
  rejectedRows: any[];
  approvedBothRows: any[];
  dataErrorRows: any[];

  selectedItemsdata: any[];
  setSelectedHistoryRow: (row: any) => void;
  setOpenHistory: (open: boolean) => void;

  handleGridChange: (data?: any) => void;
  onRowSelected: (row?: any) => void;

  handleHeaderTakeActionClick: () => void;
  handleSubmitClick: () => void;
  currency: "USD" | "LOCAL";
  setCurrency: (value: "USD" | "LOCAL") => void;
};

export function useEndUserOutletContext() {
  return useOutletContext<EndUserOutletContext>();
}

// Generic builder utilities (reusable)
const toFieldName = (key) => {
  if (!key) return key;
  const parts = key.split(" ");
  parts[0] = parts[0].charAt(0).toLowerCase() + parts[0].slice(1);
  return parts.join("");
};

// Helper function to get the numeric field name for sorting
const getNumericFieldName = (originalHeader) => {
  return toFieldName(originalHeader) + "_numeric";
};

// Fields that should be sorted as dates
const dateSortFields = [
  "Submission Date",
  "Approval/Rejection Date",
  "Extraction Date",
];

// Create a comparator function for date fields
const createDateComparator = () => {
  return (valueA, valueB) => {
    const toTime = (v) => {
      if (!v || v === "-") return 0;
      const t = new Date(v).getTime();
      return isNaN(t) ? 0 : t;
    };
    return toTime(valueA) - toTime(valueB);
  };
};

// Fields that have numeric values stored separately for proper sorting
const calculatedNumericFields = [
  "Variation IO vs S4 %",
  "VAR (%) - Approved SS vs S4",
  "VAR (wk) - Approved SS vs S4",
  "VAR (wk) - Approved SS vs IO",
  "VAR (%) - Approved SS vs IO",
  "VAR (value) Approved SS Value vs S4",
  "VAR (value) Approved SS  vs IO",
  "Approved SS value",
];

// Create a comparator function for fields with numeric values
const createNumericComparator = (originalHeader) => {
  return (valueA, valueB) => {
    const toSortableNumber = (value) => {
      if (value === null || value === undefined) return 0;
      const text = String(value).trim();
      if (!text || text === "-" || text.toUpperCase() === "N/A") return 0;

      // Handle negatives shown as (1,234.56)
      const isParenNegative = /^\(.*\)$/.test(text);
      const normalized = text
        .replace(/[(),%\s]/g, "")
        .replace(/[^\d.-]/g, "");

      if (!normalized || normalized === "-" || normalized === "." || normalized === "-.") {
        return 0;
      }

      const parsed = Number(normalized);
      if (!Number.isFinite(parsed)) return 0;
      return isParenNegative ? -Math.abs(parsed) : parsed;
    };

    const numA = toSortableNumber(valueA);
    const numB = toSortableNumber(valueB);
    return numA - numB;
  };
};

// Maps a currency code (e.g. "EUR", "GBP") to its symbol (€, £) using the browser Intl API
const getCurrencySymbol = (code: string): string => {
  if (!code) return "";
  try {
    return (0)
      .toLocaleString("en", {
        style: "currency",
        currency: code,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })
      .replace(/[\d,\s.]/g, "")
      .trim();
  } catch {
    return code;
  }
};

const makeChildCol = (
  originalHeader,
  shortLabel,
  customRendererFor = [],
  headerClass?,
) => {
  const col = {
    field: toFieldName(originalHeader),
    headerName: shortLabel,
    originalHeader,
    cellRenderer: customRendererFor.includes(originalHeader)
      ? CustomCellRenderer
      : undefined,
    sortable: true,
    filter: true,
    resizable: true,
    headerClass: "ag-header-cell-label",
  };

  // Add comparator for calculated numeric fields
  if (calculatedNumericFields.includes(originalHeader)) {
    col["comparator"] = createNumericComparator(originalHeader);
  }

  return col;
};

/**
 * buildColumnGroups - generic, reusable function to generate column groups
 * @param {string[]} allKeys - available headers from data
 * @param {Array} groupConfigs - array of { headerName, children } where children is array of { matcher: fn | string | RegExp, label }
 *   matcher can be a string (substring), RegExp, or a function(header)=>boolean
 * @param {Array} customRendererFor - list of originalHeader names that need custom renderer
 * @returns { object } { groupColumnDefs, groupedOriginalHeaders }
 */
const buildColumnGroups = (
  allKeys = [],
  groupConfigs = [],
  customRendererFor = [],
) => {
  const groupColumnDefs = groupConfigs
    .map((cfg) => {
      const children = (cfg.children || [])
        .map((child) => {
          return makeChildCol(child.label, child.label, customRendererFor);
        })
        .filter(Boolean);

      return children.length
        ? {
            headerName: cfg.headerName,
            children,
            headerClass: "ag-header-group-cell-label",
          }
        : null;
    })
    .filter(Boolean);

  return { groupColumnDefs };
};

const customCells = [
  "Safety Stock (Qty) - End User Proposal",
  "Coverage Profile (wk) - End User Proposal",
  "Safety Time (wk) - End User Proposal",
];

export const EndUserView = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const queryParams = new URLSearchParams(location.search);
  const rawPlant = queryParams.get("plant"); // "5040 - Ballina" or "5040"
  const plantFromUrl = rawPlant?.split("-")[0]?.trim() || "5040"; // "5040"
  const regionFromUrl = queryParams.get("region");
  const [planerViewData, setPlanerViewData] = useState<PlannerViewItem[]>([]);
  const currentRole = useSelector(
    (state: any) => state?.userRoles?.currentRole || null,
  );
  const isRoleReady = Boolean(currentRole);
  const canSubmitPlanner =
    isRoleReady && hasRolePermission(currentRole, "submit_planner");
  const { enqueueSnackbar } = useSnackbar();
  const selectedPlant = useSelector(
    (state: any) => state.userRoles.selectedPlant || [],
  );

  const [planerOutputViewData, setPlanerOutputViewData] = useState<
    PlannerOutputViewResponse[]
  >([]);
  // Separate loading states for input and output views
  const [inputLoading, setInputLoading] = useState(false);
  const [outputLoading, setOutputLoading] = useState(false);
  const [warningPopup, setWarningPopup] = useState({ open: false, data: null });
  const isAnySubmittingRef = useRef(false);

  const [hiddenIds, setHiddenIds] = useState<Set<string>>(new Set()); //  add
  const [lastSubmittedMaterial, setLastSubmittedMaterial] =
    useState<string>(""); //  add
  const isSubmitted = useSelector(
    (state: RootState) => state.userRoles.isSubmitted,
  );
  const submittedDataItem = useSelector(
    (state: RootState) => state.userRoles.submittedDataItem,
  );
  const { upsertItem, removeItem, clear, items } = useArrayData();
  const theme = useTheme();

  const inputRef = useRef();
  // page state removed (was unused)
  const [splitView, setSplitView] = useState(false);
  const [actionItemsView, setActionItemsView] = useState(0);
  const [minimized, setMinimized] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currency, setCurrency] = useState<"USD" | "LOCAL">("USD");
  const gridRef = useRef<AgGridReact>(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [openHistory, setOpenHistory] = useState(false);
  const [selectedHistoryRow, setSelectedHistoryRow] = useState(null);
  const { items: selectedItemsdata } = useArrayData();
  // Prevent duplicate fetches during initial hydration and repeated plant updates
  const lastFetchedPlantRef = useRef<string | null>(null);
  const lastFetchedOutputKeyRef = useRef<string | null>(null);

  const fetchPlannerViewData = async (selectedData?: any) => {
    setInputLoading(true);
    dispatch(setApiCall(true));
    try {
      if (selectedData) {
        const selectedItemList = Array.isArray(selectedData)
          ? selectedData
          : [selectedData];
        const removeIds = new Set(selectedItemList?.map((b) => b.inputId)); // O(m)
        const updatedData = planerViewData.filter(
          (a) => !removeIds.has(a.inputId),
        ); // O(n)
        setPlanerViewData(updatedData);
      } else {
        const data = await getPlannerView(plantFromUrl);
        if (data) {
          setPlanerViewData(data);
        }
      }
    } catch (e) {
      setPlanerViewData([]);
    } finally {
      setInputLoading(false);
      dispatch(setSubmittedData());
      dispatch(setApiCall(false));
    }
  };
  const fetchOutputViewData = async () => {
    setOutputLoading(true);
    dispatch(setApiCall(true));
    try {
      const data = await getPlannerOutputView(plantFromUrl);

      setPlanerOutputViewData(data);
    } catch (e) {
      setPlanerOutputViewData([]);
    } finally {
      setOutputLoading(false);
      dispatch(setApiCall(false));
    }
  };

  useEffect(() => {
    // If selectedPlant is empty, clear the table data
    if (!selectedPlant || selectedPlant.length === 0) {
      setPlanerViewData([]);
      setPlanerOutputViewData([]);
      lastFetchedPlantRef.current = null;
      lastFetchedOutputKeyRef.current = null;
      return;
    }
    const selectedPlantKey = selectedPlant.join(",");
    // Only refetch if not first mount
    if (
      selectedPlant &&
      selectedPlant.length > 0 &&
      location.pathname === "/endUserView/outputView"
    ) {
      const outputKey = `${plantFromUrl}|${selectedPlantKey}`;
      if (lastFetchedOutputKeyRef.current === outputKey) return;
      lastFetchedOutputKeyRef.current = outputKey;
      fetchOutputViewData();
    }
    if (
      selectedPlant &&
      selectedPlant.length > 0 &&
      location.pathname === "/endUserView/input"
    ) {
      const inputKey = `${plantFromUrl}|${selectedPlantKey}`;
      if (lastFetchedPlantRef.current === inputKey) return;
      lastFetchedPlantRef.current = inputKey;
      fetchPlannerViewData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlant, location.pathname, plantFromUrl]);
  useEffect(() => {
    if (!isSubmitted) return;
    fetchPlannerViewData(submittedDataItem);
  }, [isSubmitted]);

  useEffect(() => {
    if (location.pathname !== "/endUserView/input") {
      lastFetchedPlantRef.current = null;
    }
    if (location.pathname !== "/endUserView/outputView") {
      lastFetchedOutputKeyRef.current = null;
    }
  }, [location.pathname]);

  const apiInputRows = useMemo(() => {
    return planerViewData.map((item) => {
      const s = item.safetyStockSource;
      const approver = item.approvers?.[0];
      const mappedApprovers = approver ? approver.approverId : "";

      return {
        __raw: item, //  ADD (hidden, not a column)
        mappedApprovers,
        inputId: item.inputId,

        "Approval Status": item.lineStatus,
        "IO Run Type": s.ioRunTypeLabel,
        "Extraction Date": s.ioExtractionDate,
        "MRP controller": s.mrpControllerId,
        "MRP controller desc": s.mrpControllerDescription ?? "",
        "BCP flag": s.bpcFlag ?? "",
        "Material Plant": s.materialPlant,
        "Planning Lead": s.planningLead ?? "",
        "IO Material Type": s.ioMaterialType,
        "Material Number": s.materialNumber,
        "Material Description": s.materialDescription,
        "Approval Required": s.approval_required,
        "Review Flag for IO Writeback": s.reviewRequired,
        "Pricing Status": item.pricingError ?? "Available",

        // S4 (formatted)
        "S4 Safety Stock (Qty)": formatValueNoDecimals(s.s4SafetyStockQty),
        "S4 Safety Time(weeks)": formatOneDecimal(s.s4SafetyTime),
        "S4 Coverage profile(weeks)": formatOneDecimal(s.s4CoverageProfile),
        "S4 Consolidated SS(weeks)": formatOneDecimal(
          s.s4ConsolidatedSafetyStock,
        ),

        // S4 (raw)
        s4SafetyStockQtyRaw: s.s4SafetyStockQty,
        s4SafetyTimeRaw: s.s4SafetyTime,
        s4CoverageProfileRaw: s.s4CoverageProfile,
        s4ConsolidatedSafetyStockRaw: s.s4ConsolidatedSafetyStock,

        // IO (formatted)
        "IO Safety Time (Weeks) (Rounded)": formatOneDecimal(s.ioSafetyTime),
        "IO Coverage profile(weeks)": formatOneDecimal(s.ioCoverageProfile),
        "IO Consolidated SS(weeks)": formatOneDecimal(
          s.ioConsolidatedSafetyStockRounded,
        ),
        "IO Safety Stock (Qty)": formatValueNoDecimals(item?.ioSafetyStockQty),

        // IO (raw)
        ioSafetyTimeRaw: s.ioSafetyTime,
        ioCoverageProfileRaw: s.ioCoverageProfile,
        ioConsolidatedSafetyStockRaw: s.ioConsolidatedSafetyStockRounded,
        ioSafetyStockQtyRaw: item?.ioSafetyStockQty,

        Approver: approver?.approverName ?? "",
        "End User Actions": "",
        "Coverage Profile deviation (%)":
          item?.coverageProfileDeviationPct ?? null,
        "Safety Time deviation (%)": item?.safetyTimeDeviationPct ?? null,
        "Safety Stock Qty deviation (%)":
          item?.safetyStockQtyDeviationPct ?? null,
      };
    });
  }, [planerViewData]);

  const WarningButtonRenderer = (props) => {
    if (!canSubmitPlanner) {
      return null;
    }

    const handleClick = () => {
      console.log("props.data", props.data);
      if (isAnySubmittingRef.current) return;
      setWarningPopup({ open: true, data: props.data });
    };

    return (
      <ButtonComponent
        className="take-action"
        type="secondary"
        label="Take Action"
        onClick={handleClick}
        disabled={isAnySubmittingRef.current}
        borderRadius="28px"
      />
    );
  };

  const handleHeaderTakeActionClick = () => {
    if (!canSubmitPlanner) return;
    const selected = selectedItemsdata ?? [];
    if (selected.length) {
      setWarningPopup({ open: true, data: selected });
    }
  };

  const makeCol = (key, customRendererFor = []) => {
    const col = {
      field: toFieldName(key),
      headerName: key,
      originalHeader: key,
      cellRenderer:
        key === "End User Actions"
          ? WarningButtonRenderer
          : customRendererFor.includes(key)
            ? CustomCellRenderer
            : undefined,
      hide: key === "End User Actions" && !canSubmitPlanner,
      sortable: true,
      filter: true,
      resizable: true,
    };

    // Add comparator for calculated numeric fields
    if (calculatedNumericFields.includes(key)) {
      col["comparator"] = createNumericComparator(key);
    }

    // Add date comparator for date fields
    if (dateSortFields.includes(key)) {
      col["comparator"] = createDateComparator();
    }

    return col;
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const openViewHistory = () => {
    setOpenHistory(true);
    handleMenuClose();
  };

  const handleSubmitClick = () => {
    enqueueSnackbar({
      message: lastSubmittedMaterial
        ? `Submitted successfully to Approver — Material ${lastSubmittedMaterial}`
        : "Submitted successfully to Approver!",
      severity: "success",
      autoHideDuration: 4000, // slightly longer for readability
    });
  };

  const [isGridReady, setGridReady] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);

  // Groups: S4, IO, Consolidated Safety Stock (weeks)
  const desiredGroupConfigs = [
    // ⬇️ Add this object at the end of desiredGroupConfigs (Input View)

    {
      headerName: "S4",
      children: [
        { label: "S4 Safety Stock (Qty)" },
        { label: "S4 Safety Time(weeks)" },
        { label: "S4 Coverage profile(weeks)" },
      ],
    },
    {
      headerName: "IO",
      children: [
        { label: "IO Safety Time (Weeks) (Rounded)" },
        { label: "IO Coverage profile(weeks)" },
      ],
    },
    {
      headerName: "Consolidated Safety Stock (weeks)",
      children: [
        { label: "IO Consolidated SS(weeks)" },
        { label: "S4 Consolidated SS(weeks)" },
        { label: "Variation IO vs S4 %" },
      ],
    },
    // {
    //   headerName: "Deviation (%)",
    //   children: [
    //     { label: "Coverage Profile deviation (%)" },
    //     { label: "Safety Time deviation (%)" },
    //     { label: "Safety Stock Qty deviation (%)" },
    //   ],
    // },
  ];

  const { columns, flatColumns } = useMemo(() => {
    const allKeys = Object.keys(apiInputRows[0] ?? {});
    const customRendererFor = customCells.slice(); // keep your custom cell renderer list

    // Build the three groups from your desired config
    const { groupColumnDefs } = buildColumnGroups(
      allKeys,
      desiredGroupConfigs,
      customRendererFor,
    );

    // Ungrouped columns at the top in exact order
    const headSequence = [
      "Extraction Date",
      "IO Run Type",
      "Approval Status",
      "MRP controller",
      "MRP controller desc",
      "BCP flag",
      // "End User",
      "Material Plant",
      "Planning Lead",
      "IO Material Type",
      "Material Number",
      "Material Description",
      "Approval Required",
      "Review Flag for IO Writeback",
    ];
    const headCols = headSequence.map((k) => makeCol(k, customRendererFor));

    // Ungrouped columns at the end in exact order
    const tailSequence = ["Pricing Status", "End User Actions", "Approver"];
    const tailCols = tailSequence.map((k) => makeCol(k, customRendererFor));

    // Final columnDefs = head + groups + tail (no extras, no remaining)
    const columnDefs: any[] = [...headCols, ...groupColumnDefs, ...tailCols];

    // Flatten for row projection
    const flattened = columnDefs.flatMap((cd) => {
      if (cd.children)
        return cd.children.map((ch: any) => ({
          field: ch.field,
          originalHeader: ch.originalHeader,
          headerName: ch.headerName,
        }));
      return [
        {
          field: cd.field,
          originalHeader: cd.originalHeader,
          headerName: cd.headerName,
        },
      ];
    });

    return { columns: columnDefs, flatColumns: flattened };
  }, [apiInputRows, canSubmitPlanner]);

  const rows = useMemo(() => {
    return apiInputRows
      .map((row, idx) => {
        const obj: any = {};

        // Map over the columns in flatColumns and match data to the row
        flatColumns.forEach((col) => {
          if (col.originalHeader && col.originalHeader in row) {
            obj[col.field] = row[col.originalHeader];
          } else {
            obj[col.field] = "";
          }
        });
        obj["inputId"] = row?.["inputId"];
        obj.pricingError = row.__raw.pricingError ?? null;
        obj["ioSafetyStockQty"] = row?.["IO Safety Stock (Qty)"];
        obj.__uid = `${row["Material Number"] ?? "mat"}__${idx}`;
        const raw = row.__raw.safetyStockSource;
        obj.ioSafetyTimeRaw = row.ioSafetyTimeRaw;
        obj.ioCoverageProfileRaw = row.ioCoverageProfileRaw;
        obj.ioConsolidatedSafetyStockRaw = row.ioConsolidatedSafetyStockRaw;
        obj.ioSafetyStockQtyRaw = row.ioSafetyStockQtyRaw;
        obj.s4SafetyStockQtyRaw = row.s4SafetyStockQtyRaw;
        obj.s4SafetyTimeRaw = row.s4SafetyTimeRaw;
        obj.s4CoverageProfileRaw = row.s4CoverageProfileRaw;
        obj.s4ConsolidatedSafetyStockRaw = row.s4ConsolidatedSafetyStockRaw;

        obj["weeklyDemandQty"] = raw.weeklyDemandQty;
        // Calculate the "Variation (%) - IO vs S4" after mapping row data
        //formula //=IF([S4 Consolidated Safety Stock (wk)]=0,0,[Variation (wk) - IO vs S4]/[S4 Consolidated Safety Stock (wk)])
        //formula Variation (wk) - IO vs S4 =[IO Consolidated Safety Stock (wk) - Rounded]-[S4 Consolidated Safety Stock (wk)]
        const ioConsolidatedSafetyStockRounded =
          Number(raw.ioConsolidatedSafetyStockRounded) || 0;
        const s4ConsolidatedSafetyStock =
          Number(raw.s4ConsolidatedSafetyStock) || 0;

        obj["variationIOvsS4week"] = parseFloat(
          (
            ioConsolidatedSafetyStockRounded - s4ConsolidatedSafetyStock
          ).toFixed(1),
        );
        const variation = parseFloat(obj["variationIOvsS4week"]) || 0;
       let variationIOvsS4Percentage = 0;

if (s4ConsolidatedSafetyStock === 0) {
  variationIOvsS4Percentage =
    ioConsolidatedSafetyStockRounded === 0 ? 0 : 100;
} else {
  variationIOvsS4Percentage =
    (variation / s4ConsolidatedSafetyStock) * 100;
}

// Store numeric value for sorting, formatted value for display
obj["variationIOvsS4%_numeric"] = Number(
  variationIOvsS4Percentage.toFixed(0),
);

obj["variationIOvsS4%"] =
  Math.round(variationIOvsS4Percentage) + "%";
        obj["approverId"] = row.mappedApprovers;
        return obj; // Return the row object with the calculated value
      })
      .filter((row) => row["approvalStatus"] === "Reviewing IO Output");
  }, [apiInputRows, flatColumns]);

  const submittedIds = useMemo(() => {
    return new Set(
      (items ?? [])
        .filter((i) => String(i.lineStatus) === "Pending")
        .map((i) => String(i.id)),
    );
  }, [items]);

  const inputRows = useMemo(() => {
    return rows.filter((r) => !submittedIds.has(String(r.materialNumber)));
  }, [rows, submittedIds]);

  const desiredOutputGroupConfigs = [
    // ⬇️ Add this object at the end of desiredOutputGroupConfigs (Output View)

    {
      headerName: "End User Proposal",
      children: [
        {
          label: "Total consolidated Safety Stock (weeks)-EU",
          field: "Total consolidated Safety Stock (weeks)-EU",
        },
        { label: "Safety Time (weeks)-EU", field: "Safety Time (weeks)-EU" },
        {
          label: "Coverage profile(weeks)-EU",
          field: "Coverage profile(weeks)-EU",
        },
        { label: "Safety Stock (qty)-EU", field: "Safety Stock (qty)-EU" },
      ],
    },
    {
      headerName: "Approved Safety Stock",
      children: [
        { label: "Total consolidated Safety Stock (weeks)-approved" },
        { label: "Safety Time (weeks)-approved" },
        { label: "Coverage Profile (weeks)-approved" },
        { label: "Safety Stock (qty)-approved" },
      ],
    },
    // {
    //   headerName: "Deviation (%)",
    //   children: [
    //     { label: "Coverage Profile deviation (%)" },
    //     { label: "Safety Time deviation (%)" },
    //     { label: "Safety Stock Qty deviation (%)" },
    //   ],
    // },
  ];

  const apiOutputRows = useMemo(() => {
    return planerOutputViewData.map((item) => {
      return {
        // "Approval status": item.lineStatus,
        "BCP flag": item?.bpcFlag ?? "",
        "Material Plant": item.materialPlant,
        "Planning Lead": item.planningLead ?? "",
        "Material Number": item.materialNumber,
        "Material Description": item.materialDescription,
        Approver: item?.approverName ?? "",
        "approval Required": item?.approval_required || "",
        "Review Flag for IO Writeback": item?.reviewRequired ?? "",
        "final selected choice": item.approverSelectedChoice || "",
        "Comments from the approver": item.approverComments || "",
        //  "Reason": item.reason || "",
        //  "IO Material Type":item.ioMaterialType,
        //  "MRP controller desc": item.mrpControllerDescription,
        // S4
        "Safety Stock (qty)-EU": item.userSafetyStockQty,
        "Safety Time (weeks)-EU": item.userSafetyTime,
        "Coverage profile(weeks)-EU": item.userCoverageProfile,
        "Total consolidated Safety Stock (weeks)-EU":
          item.userConsolidatedSafetyStock,

        // IO
        "Safety Stock (qty)-approved": item.approvedSafetyStockQty,
        "Coverage Profile (weeks)-approved": item.approvedCoverageProfile,
        "Safety Time (weeks)-approved": item.approvedSafetyTime,
        "Total consolidated Safety Stock (weeks)-approved":
          item.approvedConsolidatedSafetyStock,

        "End User Action": item.userSelectedChoice || "",
        "Coverage Profile deviation (%)":
          item?.coverageProfileDeviationPct ?? null,
        "Safety Time deviation (%)": item?.safetyTimeDeviationPct ?? null,
        "Safety Stock Qty deviation (%)":
          item?.safetyStockQtyDeviationPct ?? null,
        "Submission Date": item?.submitted_at ?? null,
        // Add Updated Date column
        "Approval/Rejection Date": item.approvedTimestampString ?? "",
      };
    });
  }, [planerOutputViewData]);

  const { columns: outputColumns, flatColumns: flatOutputColumns } =
    useMemo(() => {
      const allKeys = Object.keys(apiOutputRows[0] ?? {});

      const customRendererFor = customCells.slice(); // keep your custom cell renderer list

      // Build the three groups from your desired config
      const { groupColumnDefs } = buildColumnGroups(
        allKeys,
        desiredOutputGroupConfigs,
        customRendererFor,
      );

      // OUTPUT
      const headSequence = [
        "Extraction Date",
        "IO Run Type",
        "Approval status",
        "MRP controller",
        "MRP controller desc",
        "BCP flag",
        "Approver",
        "Material Plant",
        "Planning Lead",
        "IO Material Type",
        "Material Number",
        "Material Description",
        "End User Action",
        "End User Comments",
        "Reason",
        "Approval Required",
        "Review Flag for IO Writeback",
        "Approver Reason Code",
      ];
      const headCols = headSequence.map((k) => makeCol(k, customRendererFor));

      // Ungrouped columns at the end in exact order
      const tailSequence = [
        "Comments from the approver",
        // "Reason",
        "VAR (%) - Approved SS vs S4",
        "VAR (wk) - Approved SS vs S4",
        "VAR (%) - Approved SS vs IO",
        "VAR (wk) - Approved SS vs IO",
        "Approved SS value",
        "VAR (value) Approved SS Value vs S4",
        "VAR (value) Approved SS  vs IO",
        // "Currency",
        "Final selected choice",
        "Final Action",
        "Submission Date",
        "Approval/Rejection Date",
      ];
      const tailCols = tailSequence.map((k) => makeCol(k, customRendererFor));

      // Final columnDefs = head + groups + tail (no extras, no remaining)
      const columnDefs: any[] = [...headCols, ...groupColumnDefs, ...tailCols];

      // Flatten for row projection
      const flattened = columnDefs.flatMap((cd) => {
        if (cd.children)
          return cd.children.map((ch: any) => ({
            field: ch.field,
            originalHeader: ch.originalHeader,
            headerName: ch.headerName,
          }));
        return [
          {
            field: cd.field,
            originalHeader: cd.originalHeader,
            headerName: cd.headerName,
          },
        ];
      });

      return { columns: columnDefs, flatColumns: flattened };
    }, []);

  const approvedRows = useMemo(() => {
    const sortedData = [...planerOutputViewData].sort((a, b) => {
      const timeA = a.approvedDate ? new Date(a.approvedDate).getTime() : 0;
      const timeB = b.approvedDate ? new Date(b.approvedDate).getTime() : 0;
      return timeB - timeA; // latest first
    });
    return sortedData.map((row, idx) => {
      const obj = {};
      const r = { ...row }; // ADD THIS LINE
      // Flatten columns and iterate through them to map headers to actual data fields
      flatOutputColumns
        .flatMap((c: any) => (c.children ? c.children : [c])) // Flatten columns in case of grouped columns
        .forEach((leaf: any) => {
          const header = leaf.headerName || leaf.field;

          // Your formula logic for the columns
          const weeklyDemand = Number(
            parseNumberOrText(r?.["weeklyDemandQty"]),
          );
          const approvedSafetyStockQty = Number(
            parseNumberOrText(r?.["approvedSafetyStockQty"]),
          );
          const coverageProfileApproved = Number(
            parseNumberOrText(r?.["approvedCoverageProfile"]),
          );
          const safetyTimeApproved = Number(
            parseNumberOrText(r?.["approvedSafetyTime"]),
          );
          obj["__uid"] = `${r.materialNumber ?? "mat"}__${idx}`;
          const showVarFields =
            r.lineStatus === "End User SS Approved" ||
            r.lineStatus === "End User SS Rejected";
          obj["approvedConsolidatedSafetyStock"] =
            weeklyDemand === 0
              ? coverageProfileApproved + safetyTimeApproved
              : approvedSafetyStockQty / weeklyDemand +
                coverageProfileApproved +
                safetyTimeApproved;

          const consolidateSSApproved = Number(
            parseNumberOrText(obj?.["approvedConsolidatedSafetyStock"]),
          );
          const s4ConsolidatedSS: number = Number(
            parseNumberOrText(r?.["s4ConsolidatedSafetyStock"]),
          );

          const varWkApprovedVsS4: number =
            consolidateSSApproved - s4ConsolidatedSS;

          // Store numeric value for sorting
          let varPctApprovedSSvsS4Numeric = 0;

if (s4ConsolidatedSS === 0) {
  varPctApprovedSSvsS4Numeric =
    consolidateSSApproved === 0 ? 0 : 100;
} else {
  varPctApprovedSSvsS4Numeric =
    (varWkApprovedVsS4 / s4ConsolidatedSS) * 100;
}

obj["varPctApprovedSSvsS4_numeric"] = Number(
  varPctApprovedSSvsS4Numeric.toFixed(2),
);

obj["varPctApprovedSSvsS4"] = varPctApprovedSSvsS4Numeric;

          obj["varWkApprovedSSvsS4_numeric"] = Number(
            varWkApprovedVsS4.toFixed(2),
          ); // Numeric for sorting
          obj["varWkApprovedSSvsS4"] = varWkApprovedVsS4;

          // Calculate VAR (wk) - Approved SS vs IO
          const IoConsolidatedSSRounded =
            Number(
              parseNumberOrText(r?.["ioConsolidatedSafetyStockRounded"]),
            ) || 0;
          obj["varWKApprovedSSvsIO"] =
            consolidateSSApproved - IoConsolidatedSSRounded;

          // Calculate VAR (%) - Approved SS vs IO
          let varPctApprovedSSvsIONumeric = 0;

if (IoConsolidatedSSRounded === 0) {
  varPctApprovedSSvsIONumeric =
    consolidateSSApproved === 0 ? 0 : 100;
} else {
  varPctApprovedSSvsIONumeric =
    (obj["varWKApprovedSSvsIO"] / IoConsolidatedSSRounded) * 100;
}

obj["varPctApprovedSSvsIO_numeric"] = Number(
  varPctApprovedSSvsIONumeric.toFixed(2),
);

obj["varPctApprovedSSvsIO"] = varPctApprovedSSvsIONumeric;
          obj["varWKApprovedSSvsIO_numeric"] = Number(
            obj["varWKApprovedSSvsIO"].toFixed(2),
          ); // Numeric for sorting

          //VAR (USD) - Approved SS vs S4
          //formula [Consolidated Safety Stock (USD) - Approved]-[Consolidated Safety Stock (USD) - S4]
          //formula Consolidated Safety Stock (USD) - Approved: [Consolidated Safety Stock (wk) - Approved]*[Weekly Demand (Qty)]*[Std Price (USD/Qty)]
          //[S4 Consolidated Safety Stock (wk)]*[Weekly Demand (Qty)]*[Std Price (USD/Qty)]
          const approvedConsolidatedSafetyStock = Number(
            parseNumberOrText(obj["approvedConsolidatedSafetyStock"]),
          );
          const pricePerUnit = Number(parseNumberOrText(r?.["pricePerUnit"]));
          const exchangeRate = Number(parseNumberOrText(r?.["exchangeRate"]));
          const getExchangeValue = () => {
            let exchangeValue;
            if (exchangeRate < 0) {
              exchangeValue = pricePerUnit / exchangeRate;
            } else {
              exchangeValue = pricePerUnit * exchangeRate;
            }
            return exchangeValue;
          };

          const stdPricePerQty =
            pricePerUnit <= 0
              ? 0
              : currency === "LOCAL"
                ? row.standardPrice / pricePerUnit
                : exchangeRate >= 0
                  ? (row.standardPrice * exchangeRate) / pricePerUnit
                  : row.standardPrice / (pricePerUnit * exchangeRate);

          const s4ConsolidatedSafetyStock = Number(
            parseNumberOrText(r?.["s4ConsolidatedSafetyStock"]),
          );
          obj["consolidatedSafetyStockUsdApproved"] =
            approvedConsolidatedSafetyStock * weeklyDemand * stdPricePerQty;
          obj["consolidatedSafetyStockUsdS4"] =
            s4ConsolidatedSafetyStock * weeklyDemand * stdPricePerQty;

          // Store numeric value for sorting
          obj["varUsdApprovedSSvsS4_numeric"] = Number(
            (
              obj["consolidatedSafetyStockUsdApproved"] -
              obj["consolidatedSafetyStockUsdS4"]
            ).toFixed(2),
          );
          obj["varUsdApprovedSSvsS4"] =
            obj["consolidatedSafetyStockUsdApproved"] -
            obj["consolidatedSafetyStockUsdS4"];
          // VAR (USD) - Approved SS vs IO //wrong
          // VAR (USD) - Approved SS vs IO
          // formula = [Consolidated Safety Stock (USD) - Approved] - [Consolidated Safety Stock (USD) - IO]

          const consolidatedSafetyStockApproved = Number(
            parseNumberOrText(obj?.["approvedConsolidatedSafetyStock"]),
          );
          const ioConsolidatedSafetyStockRounded = Number(
            parseNumberOrText(r?.["ioConsolidatedSafetyStockRounded"]),
          );

          // Consolidated Safety Stock (USD) - Approved
          obj["consolidatedSafetyStockUsdApproved"] =
            consolidatedSafetyStockApproved * weeklyDemand * stdPricePerQty;

          // Consolidated Safety Stock (USD) - IO
          obj["consolidatedSafetyStockUsdIO"] =
            ioConsolidatedSafetyStockRounded * weeklyDemand * stdPricePerQty;

          // VAR (USD) - Approved SS vs IO
          // Store numeric value for sorting
          obj["varUsdApprovedSSvsIO_numeric"] = Number(
            (
              obj["consolidatedSafetyStockUsdApproved"] -
              obj["consolidatedSafetyStockUsdIO"]
            ).toFixed(2),
          );
          obj["varUsdApprovedSSvsIO"] = Number(
            obj["consolidatedSafetyStockUsdApproved"] -
              obj["consolidatedSafetyStockUsdIO"],
          ).toFixed(2);

          // approved ss value - store numeric only, formatted in switch statement
          obj["approvedSSValue_numeric"] = Number(
            obj["consolidatedSafetyStockUsdApproved"].toFixed(2),
          );
          obj["approvedSSValue"] =
            obj["consolidatedSafetyStockUsdApproved"] || 0;

          // Switch statement to map the header to the corresponding field in the row data (r)
          switch (
            header // Use .trim() to avoid extra spaces causing issues
          ) {
            case "Material Number":
              obj[header] = r.materialNumber;
              break;
            case "Material Plant":
              obj[header] = r.materialPlant;
              break;
            case "Planning Lead":
              obj[header] = r.planningLead || "";
              break;
            case "BCP flag":
              obj[header] = r.bpcFlag ?? ""; // Map bpcFlag to BCP flag, use empty string if null or undefined
              break;
            case "Approver":
              obj[header] = r.approverName; // Map approverName to Approver
              break;
            case "Material Description":
              obj[header] = r.materialDescription;
              break;
            case "Approval Required":
              obj[header] = r.approval_required || "";
              break;
            case "Review Flag for IO Writeback":
              obj[header] = r.reviewRequired ?? "";
              break;
            case "Approver Reason Code":
              obj[header] = r.approverReasonCode || "";
              break;
            case "Reason":
              obj[header] = r.userReasonCode || "";
              break;
            case "Safety Stock (qty)-EU":
              obj[header] = formatValueNoDecimals(r.userSafetyStockQty || 0); // Map userSafetyStockQty to Safety Stock (EU)
              break;
            case "Coverage profile(weeks)-EU":
              obj[header] = formatOneDecimal(r.userCoverageProfile || 0); // Map userCoverageProfile to Coverage Profile (EU)
              break;
            case "Safety Time (weeks)-EU":
              obj[header] = formatOneDecimal(r.userSafetyTime || 0); // Map userSafetyTime to Safety Time (EU)
              break;
            case "Coverage Profile (weeks)-approved":
              obj[header] = formatOneDecimal(r.approvedCoverageProfile || 0);
              break;
            case "Safety Time (weeks)-approved":
              obj[header] = formatOneDecimal(r.approvedSafetyTime || 0);
              break;
            case "Safety Stock (qty)-approved":
              obj[header] = formatValueNoDecimals(
                r.approvedSafetyStockQty || 0,
              );
              break;
            case "Final selected choice":
              obj[header] = r.approverSelectedChoice || ""; // Map userSelectedChoice to Selected Choice
              break;
            case "Final Action":
              obj[header] = r.finalAction || "";
              break;
            case "Approval/Rejection Date":
              obj[header] = r.approvedTimestampString || "";
              break;
            case "Submission Date":
              obj[header] = r.submitted_at || "";
              break;
            case "Comments from the approver":
              obj[header] = r.approverComments || ""; // Map approverComments to Comments from approver
              break;
            case "Extraction Date":
              obj[header] = r.ioExtractionDate || ""; // Map ioExtractionDate to Extraction Date
              break;
            case "MRP controller":
              obj[header] =
                r.mrpControllerId !== null && r.mrpControllerId !== undefined
                  ? String(r.mrpControllerId) // convert number to string
                  : "-";
              break;
            case "MRP controller desc":
              obj[header] = r.mrpControllerDescription || ""; // Map mrpControllerDescription to MRP controller desc
              break;
            case "Total consolidated Safety Stock (weeks)-EU":
              obj[header] = formatOneDecimal(
                r.userConsolidatedSafetyStock || 0,
              ); // Map userConsolidatedSafetyStock to Total consolidated Safety Stock (weeks)-EU
              break;
            case "Total consolidated Safety Stock (weeks)-approved":
              obj[header] = formatOneDecimal(
                r.approvedConsolidatedSafetyStock || 0,
              ); // Map approvedConsolidatedSafetyStock to Total consolidated Safety Stock (weeks)-approved
              break;
            case "IO Material Type":
              obj[header] = r.ioMaterialType || ""; // Map ioMaterialType to IO Material Type
              break;
            case "Approval status":
              obj[header] = r.lineStatus || ""; // Map lineStatus to Line Status
              break;
            case "IO Run Type":
              obj[header] = r.ioRunTypeLabel || ""; // Map ioRunTypeLabel to Run Type
              break;
            case "End User Action":
              obj[header] = r.userSelectedChoice; // Placeholder for End User Actions
              break;
            case "End User Comments":
              obj[header] = r.userComments; // Placeholder for End User Comments
              break;
            case "Approved SS value":
              obj[header] =
                (currency === "USD"
                  ? "$"
                  : getCurrencySymbol(r.currency ?? "")) +
                formatTwoDecimals(
                  formatValueNoDecimals(obj["approvedSSValue_numeric"]) || 0,
                );
              break;
            case "VAR (%) - Approved SS vs S4":
  obj[header] = showVarFields
    ? formatValueNoDecimals(
        obj["varPctApprovedSSvsS4_numeric"] || 0,
      ) + "%"
    : "";
  break;
            case "VAR (wk) - Approved SS vs S4":
              obj[header] = showVarFields
                ? formatOneDecimal(obj["varWkApprovedSSvsS4_numeric"] || 0)
                : "";
              break;
            case "VAR (wk) - Approved SS vs IO":
              obj[header] = showVarFields
                ? formatOneDecimal(obj["varWKApprovedSSvsIO_numeric"] || 0)
                : "";
              break;
           case "VAR (%) - Approved SS vs IO":
  obj[header] = showVarFields
    ? formatValueNoDecimals(
        obj["varPctApprovedSSvsIO_numeric"] || 0,
      ) + "%"
    : "";
  break;
            case "VAR (value) Approved SS Value vs S4":
              obj[header] = showVarFields
                ? (currency === "USD"
                    ? "$"
                    : getCurrencySymbol(r.currency ?? "")) +
                  formatTwoDecimals(
                    formatValueNoDecimals(
                      obj["varUsdApprovedSSvsS4_numeric"],
                    ) || 0,
                  )
                : "";
              break;
            case "VAR (value) Approved SS  vs IO":
              obj[header] = showVarFields
                ? (currency === "USD"
                    ? "$"
                    : getCurrencySymbol(r.currency ?? "")) +
                  formatTwoDecimals(
                    formatValueNoDecimals(
                      obj["varUsdApprovedSSvsIO_numeric"],
                    ) || 0,
                  )
                : "";
              break;
            case "Coverage Profile deviation (%)":
              obj[header] =
                formatValueNoDecimals(r?.coverageProfileDeviationPct ?? 0) +
                "%";
              break;
            case "Safety Time deviation (%)":
              obj[header] =
                formatValueNoDecimals(r?.safetyTimeDeviationPct ?? 0) + "%";
              break;
            case "Safety Stock Qty deviation (%)":
              obj[header] =
                formatValueNoDecimals(r?.safetyStockQtyDeviationPct ?? 0) + "%";
              break;
            default:
              obj[header] = r[header] || ""; // Default mapping if header doesn't match
          }

          // Also map to the field key for ag-Grid (so it can find the column by field)
          const field = toFieldName(header); // Ensure this is the proper function to convert to field name
          if (!obj.hasOwnProperty(field)) obj[field] = obj[header];
        });

      return obj; // Return the object with all necessary fields and calculations
    });
  }, [planerOutputViewData, flatOutputColumns, currency]);

  //  Rejected items → only "Safety Stock Rejected"
  const rejectedRows = useMemo(
    () =>
      mockOutputView
        .filter((row) => row["Approval status"] === "3. Safety Stock Rejected")
        .map((row) => {
          const obj: any = {};
          outputColumns.forEach(
            (col) => (obj[col.field] = row[col.originalHeader]),
          );
          return obj;
        }),
    [outputColumns],
  );

  // Both list (Approved + Rejected) for the Approved View sub-tab
  const approvedBothRows = useMemo(() => {
    return [...approvedRows, ...rejectedRows];
  }, [approvedRows, rejectedRows]);

  // Data Error rows: filter mockOutputView for any 'Line Status' containing 'error'
  const dataErrorRows = useMemo(() => {
    return mockOutputView
      .filter((row) =>
        String(row["Approval status"] ?? "")
          .toLowerCase()
          .includes("error"),
      )
      .map((row) => {
        const obj: any = {};
        outputColumns.forEach(
          (col) => (obj[col.field] = row[col.originalHeader]),
        );
        return obj;
      });
  }, [outputColumns]);

  const sideBar = {
    toolPanels: [
      {
        id: "columns",
        labelDefault: "Columns",
        labelKey: "columns",
        iconKey: "columns",
        toolPanel: "agColumnsToolPanel",
        toolPanelParams: {
          suppressRowGroups: true,
          suppressValues: true,
          suppressPivots: true,
          suppressPivotMode: true,
        },
      },
    ],
    defaultToolPanel: "",
  };

  // (emptyRows calculation removed â€” it was unused)

  const handleEvent = (action) => {
    setMinimized((prev) => (prev === action ? "" : action));
  };

  const handleTabChange = (e, id) => {
    setActionItemsView(id);
    setSplitView(false);
  };

  const handleGridChange = (data) => {
    // console.log("grid change ", data);
  };

  // EndUserView.tsx
  const onRowSelected = (row) => {
    const id = String(row.data.materialNumber);
    const existing = (items ?? []).find((i) => String(i.id) === id);

    if (row.node.isSelected()) {
      //  Merge and preserve "Proposal Pending Approval" if already submitted
      const preservedLineStatus =
        existing?.lineStatus === "2. Proposal Pending Approval"
          ? existing.lineStatus
          : row.data.lineStatus;

      upsertItem({
        ...existing, // keep what was already in context (e.g., submitted fields)
        ...row.data, // merge what comes from the grid row
        id: row.data.materialNumber,
        lineStatus: preservedLineStatus, //  do NOT drop the pending status
      });
    } else {
      //  Do NOT remove if this item is already submitted
      if (existing?.lineStatus !== "2. Proposal Pending Approval") {
        removeItem((r) => String(r.id) === id);
      }
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Stack gap={2} padding={2} sx={{ padding: 2, paddingLeft: 0, flex: 1 }}>
        <Stack direction="row" gap={2}>
          <Typography
            sx={{
              fontSize: "20px",
              fontWeight: 600,
              // textAlign: 'enter',
              marginLeft: 1,
            }}
          >
            Safety Stock Review List
          </Typography>

          <Stack
            direction="row-reverse"
            sx={{ flex: 1, alignItems: "center" }}
            spacing={1}
          >
            {/* <ButtonBase onClick={ () => { setSplitView((prev) => !prev); setActionItemsView(0) } } sx={ {
              '&:hover *': {
                color: splitView ? 'red' : theme.palette.primary.main
              }
            } }>
              <Typography sx={ { color: splitView ? theme.palette.primary.main : '' } }>
                Split View
              </Typography>
              <SplitscreenIcon id='splitView' color={ splitView ? 'primary' : 'inherit' } />
            </ButtonBase> */}
            <>{/*  Submit Toast */}</>
          </Stack>
          {selectedItemsdata.length === 1 && (
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <IconButton onClick={handleMenuOpen}>
                <MoreVertIcon />
              </IconButton>

              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
              >
                <MenuItem onClick={openViewHistory}>View History</MenuItem>
              </Menu>
            </Box>
          )}
        </Stack>

        <Outlet
          context={{
            gridRef,
            gridTheme,
            rowsPerPage,
            setGridReady,
            sideBar,
            loading:
              location.pathname === "/endUserView/input"
                ? inputLoading
                : outputLoading,

            columns,
            outputColumns,
            inputRows,
            approvedRows,
            // rejectedRows,
            // approvedBothRows,
            dataErrorRows,

            selectedItemsdata,
            setSelectedHistoryRow,
            setOpenHistory,

            handleGridChange,
            onRowSelected,

            handleHeaderTakeActionClick,
            handleSubmitClick,
            canSubmitPlanner,
            currency,
            setCurrency,
          }}
        />
      </Stack>
      <VersionHistoryDialog
        open={openHistory}
        onClose={() => setOpenHistory(false)}
      />

      <WarningPopup
        open={warningPopup.open}
        onClose={() => setWarningPopup({ open: false, data: null })}
        //  now send an array of rows
        data={warningPopup.data} // will be any[]
        onSubmitStart={() => {
          isAnySubmittingRef.current = true;
          gridRef.current?.api?.refreshCells({ force: true });
        }}
        onSubmitEnd={() => {
          isAnySubmittingRef.current = false;
          gridRef.current?.api?.refreshCells({ force: true });
        }}
        onSubmitted={(lastId, response) => {
          if (response?.skippedMaterials?.length > 0) {
            const MAX_ITEMS = 6;
            const items = response.skippedMaterials.slice(0, MAX_ITEMS);
            const remaining = response.skippedMaterials.length - items.length;

            const messageNode = (
              <Box aria-live="polite">
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  {response?.message}
                </Typography>

                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                  {items?.map((item: any, idx: number) => (
                    <li key={item ?? idx}>
                      <Typography variant="body2">{item ?? "—"}</Typography>
                    </li>
                  ))}
                </Box>

                {remaining > 0 && (
                  <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.8 }}>
                    +{remaining} more…
                  </Typography>
                )}
              </Box>
            );

            enqueueSnackbar({
              message: messageNode,
              severity: "info",
              autoHideDuration: 4000, // slightly longer for readability
            });
          } else {
            setLastSubmittedMaterial(String(lastId));
            // setOpenSubmitToast(true);
            enqueueSnackbar({
              message: String(lastId)
                ? ["YES", "yes", true, 1].includes(
                    warningPopup?.data?.["approvalRequired"],
                  )
                  ? `Submitted successfully to Approver — Material  ${String(lastId)}`
                  : `Auto Approval Captured Successfully— Material ${String(lastId)}`
                : ["YES", "yes", true, 1].includes(
                      warningPopup?.data?.["approvalRequired"],
                    )
                  ? "Submitted successfully to Approver!"
                  : "Auto Approval Captured Successfully!",
              severity: "success",
              autoHideDuration: 4000,
            });
          }

          fetchPlannerViewData(warningPopup.data);
        }}
      />
    </ThemeProvider>
  );
};

type RenderTableProps = {
  gridRef?: any;
  gridTheme?: any;
  setGridReady?: (b: boolean) => void;
  columns?: any[];
  rows?: any[];
  rowsPerPage?: number;
  handleChangePage?: (e?: any) => void;
  sideBar?: any;
  label?: string | null;
  resizable?: boolean;
  minimized?: string | null;
  emitEvent?: (action?: string) => void;
  onChange?: (data?: any) => void;
  onRowSelected?: (row?: any) => void;
  selectedItemsData?: any[];
  setSelectedHistoryRow?: (row: any) => void;
  setOpenHistory?: (open: boolean) => void;
  loading?: boolean;
};

const RenderTable: React.FC<RenderTableProps> = ({
  gridRef,
  gridTheme,
  setGridReady,
  columns,
  rows,
  rowsPerPage,
  handleChangePage,
  sideBar,
  label,
  resizable,
  minimized,
  emitEvent = () => {},
  onChange,
  onRowSelected,
  selectedItemsData,
  setSelectedHistoryRow,
  setOpenHistory,
  loading = false,
}) => {
  const theme = useTheme();
  const selectedItem =
    selectedItemsData?.length > 0 ? selectedItemsData[0] : [];

  //  Always include a pinned selection column with checkboxes
  const finalColumns = useMemo(() => {
    const selectCol = {
      field: "__select__",
      headerName: "",
      width: 40,
      minWidth: 40,
      maxWidth: 40,
      pinned: "left",
      checkboxSelection: true, // per-row checkbox
      headerCheckboxSelection: true, // select-all in header
      suppressHeaderMenuButton: true,
      sortable: false,
      filter: false,
      resizable: false,
      // ensure clicks on the checkbox are not blocked by parent styles
      cellStyle: { textAlign: "center", pointerEvents: "auto" },
    };
    return [selectCol, ...(columns ?? [])];
  }, [columns]);

  return (
    <ConditionalWrapper
      condition={minimized !== label}
      elseContent={
        <Box paddingLeft={1}>
          <Typography>
            {label} is minimized, Click here to restore
            <IconButton onClick={() => emitEvent("")}>
              <ArrowOutwardIcon color="primary" />
            </IconButton>
          </Typography>
        </Box>
      }
    >
      <Stack
        direction="row"
        sx={{
          minHeight: resizable ? "180px" : null,
          height: resizable ? "250px" : null,
          resize: resizable ? "vertical" : null,
          overflow: resizable ? "auto" : null,
          maxHeight:
            resizable && minimized !== "" && minimized !== label
              ? null
              : resizable
                ? "325px"
                : null,
          flexGrow: resizable
            ? null
            : minimized !== "" && minimized !== label
              ? 4
              : 1,
          flexShrink: minimized === label ? 1 : 0,
        }}
      >
        <ConditionalWrapper condition={label}>
          <Stack
            gap={2}
            style={{
              fontSize: "16px",
              fontWeight: 500,
              transform: "rotate(0deg)",
              backgroundColor: "#fff",
              flex: 0,
              flexDirection: "column-reverse",
              flexWrap: "nowrap",
              alignItems: "center",
              justifyContent: "center",
              marginLeft: 2,
            }}
          >
            <Typography
              sx={{
                flex: 1,
                writingMode: "sideways-lr",
                textOrientation: "upright",
                textAlign: "center",
                display: "inline-block",
                fontSize: "16px",
                fontWeight: 500,
                padding: "4px 8px",
                whiteSpace: "nowrap",
              }}
            >
              {label}
            </Typography>
            {/* <ConditionalWrapper condition={minimized !== null}>
              <IconButton
                disabled={minimized !== "" && minimized !== "label"}
                sx={{
                  "&:hover *": {
                    color: theme.palette.primary.main,
                  },
                }}
                onClick={() => emitEvent(label!)}
              >
                <MinimizeIcon />
              </IconButton>
            </ConditionalWrapper> */}
          </Stack>
        </ConditionalWrapper>

        <Box sx={{ flex: 1, position: "relative" }}>
          {/* Loading Backdrop inside table only */}
          <Backdrop
            sx={{
              position: "absolute",
              color: "#fff",
              zIndex: 999,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
            }}
            open={loading}
          >
            <CircularProgress color="inherit" size={60} />
          </Backdrop>

          <AgGridReact
            ref={gridRef}
            onGridReady={(e) => setGridReady?.(true)}
            theme={gridTheme}
            columnDefs={finalColumns}
            rowSelection="multiple"
            suppressRowClickSelection={true}
            // optional but helpful: ensure each row has a stable ID
            getRowId={(params) => String(params.data.__uid)}
            getContextMenuItems={(params) => {
              const defaultItems = params.defaultItems ?? [];
              return [
                ...defaultItems,
                {
                  name: "View History",
                  action: () => {
                    if (selectedItem) {
                      setSelectedHistoryRow?.(selectedItem);
                      setOpenHistory?.(true);
                    } else {
                      alert("No row selected");
                    }
                  },
                  disabled: !params.node?.data,
                },
              ];
            }}
            enableCharts
            pagination
            paginationPageSize={rowsPerPage}
            paginationPageSizeSelector={[5, 10, 20, 50, 100]}
            rowModelType={"clientSide"}
            rowData={rows}
            onPaginationChanged={handleChangePage}
            gridOptions={{ sideBar }}
            onRowSelected={onRowSelected}
            stopEditingWhenCellsLoseFocus
            onCellValueChanged={onChange}
            onCellEditRequest={(e) => console.log(e.data)}
            singleClickEdit
            suppressMenuHide
            enableCellTextSelection
            suppressRowTransform
            defaultColDef={{
              sortable: true,
              filter: true,
              resizable: true,
              wrapText: true,
              autoHeight: true,
              cellStyle: {
                whiteSpace: "normal",
                lineHeight: "18px",
                textAlign: "center",
              },
              valueFormatter: (p: any) => {
                // 1) Skip for checkbox/header-checkbox columns
                const isSelectionCol =
                  p.colDef.checkboxSelection ||
                  p.colDef.headerCheckboxSelection ||
                  !p.colDef.field || // columns without a field, e.g., selection col
                  p.column.getColId() === "ag-Grid-AutoColumn";

                if (isSelectionCol) return ""; // let the checkbox render cleanly

                // 2) Your normal hyphen-for-empty logic
                const v = p.value;
                if (
                  v === null ||
                  v === undefined ||
                  (typeof v === "string" && v.trim() === "") ||
                  (typeof v === "number" && Number.isNaN(v))
                ) {
                  return "-";
                }
                return v;
              },
            }}
          />
        </Box>
      </Stack>
    </ConditionalWrapper>
  );
};

// const [warningPopup, setWarningPopup] = useState({ open: false, data: null });
type CustomCellRendererProps = {
  value: any;
  data: any;
  colDef: any;
  setValue: (v: any) => void;
};

const CustomCellRenderer: React.FC<CustomCellRendererProps> = ({
  value,
  data,
  colDef,
  setValue,
}) => {
  const extractCells = [
    "S4 Safety Stock (Qty)",
    "S4 Coverage Profile (wk)",
    "S4 Safety Time (wk)",
    "IO Consolidated Safety Stock (wk) - Rounded",
    "IO Coverage (Weeks) (Rounded)",
    "IO Safety Time (Weeks) (Rounded)",
  ];

  const commonCells = ["Safety Stock", "Coverage Profile", "Safety Time"];

  const [isCustom] = useState(false);
  const [popup, setPopup] = useState({
    open: false,
    label: "",
    key: "",
    value: "",
  });
  const [openToast, setOpenToast] = useState(false);

  const cellKeys = extractCells.map((key) => {
    const updatedKey = key.split(" ");
    updatedKey[0] =
      updatedKey[0].charAt(0).toLowerCase() + updatedKey[0].slice(1);
    return { key: updatedKey.join(""), label: key };
  });

  const longHeader = colDef.originalHeader || colDef.headerName || "";
  const matchedCommon = commonCells.find(
    (c) => longHeader.includes(c) || colDef.headerName.includes(c),
  );
  const matched =
    matchedCommon ||
    (longHeader.includes("Coverage")
      ? "Coverage Profile"
      : longHeader.includes("Safety Time")
        ? "Safety Time"
        : "Safety Stock");

  const cells = cellKeys
    .filter((obj) => {
      if (matched === "Safety Stock") return obj.label.includes("Safety Stock");
      if (matched === "Coverage Profile") return obj.label.includes("Coverage");
      return (
        obj.label.includes("Safety Time") || obj.label.includes("Safety Week")
      );
    })
    .map((val) => ({
      ...val,
      label: `${val.key.includes("s4") ? "S4" : "IO"} ${
        matched === "Coverage Profile"
          ? "Coverage"
          : matched.replace(" (wk)", "")
      }`,
    }));

  const handleChipEvent = (e, key, label) => {
    e.preventDefault();
    setPopup({
      open: true,
      label: key === "custom" ? "?" : label,
      key,
      value: data[key],
    });
  };

  const handleSaveValue = (newVal) => {
    if (setValue) setValue(newVal);
    setPopup({ ...popup, open: false });
  };
  const { upsertItem } = useArrayData();

  return (
    <>
      <ConditionalWrapper
        condition={value === "" && !isCustom}
        elseContent={
          <input
            type="text"
            style={{ border: "none", background: "transparent" }}
            value={value}
            onChange={(e) => setValue && setValue(e.target.value)}
          />
        }
      >
        {/* {cells.map((cell, idx) => (
          <IconButton key={cell.key} onClick={(e) => handleChipEvent(e, cell.key, cell.label)}>
            <Chip variant="filled" size='small' color={idx === 0 ? 'primary' : 'secondary'} label={cell.label} />
          </IconButton>
        ))}
        <IconButton key='custom' onClick={(e) => handleChipEvent(e, 'custom', '?')}>
          <Chip variant="filled" size='small' color='warning' label='?' />
        </IconButton> */}

        {/* <IconButton onClick={ (e) => handleChipEvent(e, "warning", "Warning") }> */}
        <WarningIcon sx={{ color: "#f44336", fontSize: 24 }} />
        {/* </IconButton> */}
      </ConditionalWrapper>

      <ChipPopup
        open={popup.open}
        label={popup.label}
        value={popup.value}
        onSave={handleSaveValue}
        onClose={() => setPopup((prev) => ({ ...prev, open: false }))}
        data={data}
        onSubmitToApprover={(row) => {
          upsertItem({
            ...row,
            id: row.materialNumber, // SAME as AG-Grid onRowSelected
          });
        }}
        onShowToast={() => setOpenToast(true)} // 👈 NEW
      />
      <Snackbar
        open={openToast}
        autoHideDuration={3000}
        onClose={() => setOpenToast(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity="success"
          variant="filled"
          sx={{
            backgroundColor: "#006400",
            color: "#fff",
          }}
        >
          Submit to Approver successfully!
        </Alert>
      </Snackbar>
    </>
  );
};
